import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";

const STORY_FIELD = "Story";
const MAX_LEN = 500;

/**
 * Wellness visit: a single open-ended goals question. Ported from app-pwa Wellness.vue.
 * Unlike the pain cards, wellness has no onset/severity/body questions — just the goals note,
 * saved to the shared `Story` field so it reaches the visit summary. Continue → outcome assessment.
 */
export function WellnessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get("complaintId");
  const patientID = searchParams.get("patientID");

  const fetchComplaintValue = usePrimaryComplaintStore((s) => s.fetchComplaintValue);
  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);

  const [goals, setGoals] = useState("");
  const [saveError, setSaveError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Saves target the store's currentComplaintHeader.id, not the URL — keep them in sync so a
  // refresh (which can clear the header) still writes to the right complaint.
  useEffect(() => {
    if (complaintId == null) return;
    const state = usePrimaryComplaintStore.getState();
    if (String(state.currentComplaintHeader?.id ?? "") !== complaintId) {
      usePrimaryComplaintStore.setState({
        // Only the id is known; another complaint's type/region/name must not carry over.
        currentComplaintHeader: { complaintType: "", region: "", description: undefined, id: Number(complaintId) },
      });
    }
  }, [complaintId]);

  // Restore any previously entered goals.
  useEffect(() => {
    if (!complaintId) return;
    let cancelled = false;
    fetchComplaintValue(complaintId, STORY_FIELD)
      .then((value) => {
        if (!cancelled && typeof value === "string") setGoals(value);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [complaintId, fetchComplaintValue]);

  const persist = async (value: string) => {
    try {
      await updateComplaintValue(STORY_FIELD, value);
      setSaveError(false);
      return true;
    } catch {
      setSaveError(true);
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGoals(e.target.value.slice(0, MAX_LEN));
  };

  const goBack = () => navigate("/complaint/primary-complaint");

  const saveAndContinue = async () => {
    const ok = await persist(goals);
    if (!ok) return; // don't advance past an unsaved answer
    const search = `?complaintId=${complaintId}&patientID=${patientID ?? ""}`;
    navigate({ pathname: "/complaint/outcome-assesment", search });
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4 flex flex-col">
        <div className="complaint-history-card">
          <div className="complaint-history-heading">
            <div className="complaint-history-heading-bar" />
            <h2 className="complaint-history-heading-text">{t.wellness}</h2>
          </div>
          <div className="complaint-history-divider" />

          <div className="complaint-history-section-block">
            <p className="complaint-history-section-label normal-case font-medium">
              What are your goals for care in this office, or is there any aspect of wellness
              you&apos;d like addressed during your visits (e.g. exercise, nutrition, sleep)?
            </p>
            <textarea
              ref={textareaRef}
              className="complaint-history-textarea"
              maxLength={MAX_LEN}
              rows={6}
              placeholder="Describe your wellness goals…"
              value={goals}
              onChange={handleChange}
              onBlur={() => void persist(goals)}
            />
            <p className="complaint-history-char-count">
              {goals.length} / {MAX_LEN}
            </p>
            {saveError && (
              <p className="mt-2 text-[13px] text-healthsource-error">
                We couldn&apos;t save that just now. Check your connection — your text is still
                here; press Continue to try again.
              </p>
            )}
          </div>
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue}>
          {t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
